import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogoMark } from "../../brand/Logo";

/**
 * Site navigation, ported from the approved design canvas.
 *
 * The entrance is CSS-only: each item carries an `--i` index and hero.css
 * staggers them off `--tl-nav`. The stagger rule is scoped to `.intro-play`,
 * which `useHeroIntro` puts on <html> — so the nav animates in with the hero on
 * the landing page and simply appears everywhere else.
 *
 * No motion library here. The previous implementation used Framer Motion for
 * the dropdown; this design has no dropdown.
 */

interface NavItem {
  /** The drafting-sheet number shown above each label. */
  index: string;
  label: string;
  href: string;
}

const NAV: NavItem[] = [
  { index: "01", label: "Home", href: "/" },
  { index: "02", label: "About", href: "/about" },
  { index: "03", label: "Services", href: "/services" },
  { index: "04", label: "Projects", href: "/projects" },
  { index: "05", label: "Approach", href: "/approach" },
  { index: "06", label: "Contact", href: "/contact" },
];

function ArrowRight() {
  return (
    <svg width="24" height="12" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M0 6h22M17 1l5 5-5 5" />
    </svg>
  );
}

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Any navigation closes the menu.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  // A drawer that scrolls the page behind it reads as broken.
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  // Escape closes it, which keyboard users will expect.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <header className="iea-nav">
      <nav className="iea-nav__bar" aria-label="Primary">
        <div className="intro-nav" style={{ "--i": 0 } as React.CSSProperties}>
          <Link to="/" className="iea-nav__brand" aria-label="Indraprastha Engineers & Architects — home">
            <LogoMark height={40} />
            <span className="iea-nav__wordmark">
              <span className="iea-nav__name">Indraprastha</span>
              <span className="iea-mono iea-nav__disc">Engineers &amp; Architects</span>
            </span>
          </Link>
        </div>

        <ul className="iea-nav__links">
          {NAV.map((item, i) => (
            <li key={item.href} className="intro-nav" style={{ "--i": i + 1 } as React.CSSProperties}>
              <Link to={item.href} className="iea-nav__link">
                <span className="iea-nav__num">{item.index}</span>
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="intro-nav iea-nav__cta-wrap" style={{ "--i": 8 } as React.CSSProperties}>
          <Link to="/contact" className="iea-nav__cta">
            <span>Start a Project</span>
            <ArrowRight />
          </Link>
        </div>

        <button
          type="button"
          className="intro-nav iea-nav__burger"
          style={{ "--i": 1 } as React.CSSProperties}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="iea-nav__burgerbox" aria-hidden="true">
            <span data-open={menuOpen} />
            <span data-open={menuOpen} />
          </span>
        </button>
      </nav>

      <div className="ruler intro-nav" style={{ "--i": 0 } as React.CSSProperties} aria-hidden="true" />

      {menuOpen ? (
        <div className="iea-nav__drawer" role="dialog" aria-modal="true" aria-label="Menu">
          <ul className="iea-nav__drawerlinks">
            {NAV.map((item) => (
              <li key={item.href}>
                <Link to={item.href} className="iea-nav__drawerlink">
                  <span className="iea-nav__num">{item.index}</span>
                  <span className="iea-display iea-nav__drawertext">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
          <Link to="/contact" className="iea-nav__cta iea-nav__drawercta">
            <span>Start a Project</span>
            <ArrowRight />
          </Link>
        </div>
      ) : null}
    </header>
  );
}
